import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { MdAddCircleOutline, MdRemoveCircleOutline } from "react-icons/md";
import {
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaGlobe,
  FaShoppingBag,
  FaUserMd,
  FaHome,
  FaUtensils,
  FaShieldAlt,
  FaStar,
  FaCar,
} from "react-icons/fa";
import ClientLaunches from "./ClientLaunches";
import TypeWriter from "../TypeWriter";

const experiences = [
  {
    role: "AI/ML Intern",
    company: "DEBEL (DRDO)",
    duration: "Jul 2026 – Present",
    location: "Bengaluru, India",
    link: null,
    description:
      "Applied engineering principles to design deep-learning transformer models for real-time gait-phase analysis, collaborating with cross-functional stakeholders and using unsupervised learning to classify gait phases without labeled data.",
    tags: ["Deep Learning", "Transformers", "Unsupervised Learning", "Python", "DRDO"],
  },
  {
    role: "Full-Stack Software Engineer (Freelance)",
    company: "DigitalOrbit",
    duration: "Oct 2025 – Jun 2026",
    location: "Bengaluru, India",
    link: null,
    description:
      "Architected and deployed 15+ scalable, production-grade full-stack applications (React, Node.js) achieving 99%+ uptime through resilient system design, microservices principles, and automated CI/CD pipelines. Applied industry best practices to diagnose and refactor Core Web Vitals bottlenecks across client products, strengthening availability, load-time consistency, and performance observability.",
    clientSites: [
      {
        title: "AutoFreaks",
        domain: "autofreaks.in",
        url: "https://autofreaks.in",
        category: "Automotive Media & Tech",
        icon: <FaCar />,
        description: "Automotive media and news portal providing car reviews, vehicle specifications, industry trends, and interactive automotive content.",
        tags: ["React", "Automotive", "SEO", "Core Web Vitals"],
      },
      {
        title: "Grace Safety Engineering",
        domain: "gracesafety.in",
        url: "https://www.gracesafety.in/",
        category: "Industrial Safety & PPE",
        icon: <FaShieldAlt />,
        description: "Industrial safety equipment catalog showcasing protective gear, respiratory systems, and safety solutions with interactive product previews.",
        tags: ["React", "Tailwind CSS", "SEO", "Core Web Vitals"],
      },
      {
        title: "Sri Balaji Jewellers",
        domain: "sribalajijewellers.co.in",
        url: "https://sribalajijewellers.co.in",
        category: "E-Commerce & Fine Jewelry",
        icon: <FaStar />,
        description: "Elegant digital showcase and catalog for gold, silver, and diamond jewelry collections with responsive design and high performance.",
        tags: ["React", "Node.js", "REST API", "Responsive"],
      },
      {
        title: "Sri Raghavendra Astrologer",
        domain: "riraghavendraastrologer.in",
        url: "https://riraghavendraastrologer.in",
        category: "Vedic Astrology & Consultation",
        icon: <FaGlobe />,
        description: "Online consultation platform and service booking portal for traditional Vedic astrology and horoscope consultations.",
        tags: ["React", "Express", "SEO", "Local Business"],
      },
      {
        title: "Shrikanteshwara Jewels",
        domain: "shrikanteshwarajewels.store",
        url: "https://shrikanteshwarajewels.store",
        category: "Jewelry E-Commerce Store",
        icon: <FaShoppingBag />,
        description: "Modern online jewelry store featuring curated collections, responsive product galleries, and fast load-time consistency.",
        tags: ["React", "E-Commerce", "Tailwind CSS", "UX/UI"],
      },
      {
        title: "Aaroghyashala",
        domain: "aaroghyashala.in",
        url: "https://aaroghyashala.in",
        category: "Healthcare & Wellness",
        icon: <FaUserMd />,
        description: "Comprehensive health and wellness portal providing medical service information, holistic health packages, and patient outreach.",
        tags: ["React", "Node.js", "Healthcare", "Performance"],
      },
      {
        title: "RK Royal Interiors",
        domain: "rkroyalinteriors.in",
        url: "https://rkroyalinteriors.in",
        category: "Interior Design & Architecture",
        icon: <FaHome />,
        description: "Luxury interior design portfolio presenting residential and commercial project showcases with high-resolution visual galleries.",
        tags: ["React", "Image Gallery", "GSAP", "Core Web Vitals"],
      },
      {
        title: "SAK Furnitures",
        domain: "sak-furnitures.in",
        url: "https://sak-furnitures.in",
        category: "Custom Furniture & Decor",
        icon: <FaShoppingBag />,
        description: "Custom furniture catalog and home decor showcase integrated with instant WhatsApp enquiry and direct customer ordering.",
        tags: ["React", "Catalog", "WhatsApp API", "UX/UI"],
      },
      {
        title: "Green Chillies Restaurant",
        domain: "greenchilliesrestaurant.online",
        url: "https://greenchilliesrestaurant.online",
        category: "Dining & Food Ordering",
        icon: <FaUtensils />,
        description: "Vibrant restaurant web platform featuring digital food menus, daily specials, dining reservations, and online food ordering.",
        tags: ["React", "Food Tech", "Responsive Menu", "Performance"],
      },
    ],
    tags: ["React.js", "Node.js", "System Design", "Microservices", "CI/CD", "Core Web Vitals", "Performance Observability"],
  },
  {
    role: "Software Development Engineer",
    company: "MyStartupWave",
    duration: "Nov 2025 – Dec 2025",
    location: "Bengaluru, India",
    link: null,
    description:
      "Gathered stakeholder requirements and built a reusable React + Tailwind CSS component library, cutting UI development time by 30% across all product pages within Agile sprint cycles. Integrated frontend modules with backend REST APIs through close cross-team collaboration, reducing turnaround time by 40%.",
    tags: ["React.js", "Tailwind CSS", "REST APIs", "Component Library", "Agile"],
  },
];

function ExperienceAccordion() {
  const [openIndex, setOpenIndex] = useState(1); // Open DigitalOrbit by default

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <Container className="exp-accordion-wrapper" id="experience">
      <p className="terminal-cmd section-cmd">
        <span className="terminal-dollar">$</span>
        <TypeWriter text=" cat career.log" speed={45} />
      </p>
      <h1 className="project-heading" style={{ textAlign: "center", marginBottom: "50px" }}>
        <TypeWriter text="Professional " speed={55} cursor={false} />
        <TypeWriter text="Experience" speed={55} className="heading-accent" tag="span" />
      </h1>

      {experiences.map((exp, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} className={`exp-accordion-item ${isOpen ? "open" : ""}`}>
            {/* Header row */}
            <button
              className="exp-accordion-header"
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
            >
              <span className="exp-header-left">
                <span className="exp-role">{exp.role}</span>
                <span className="exp-company heading-accent">&nbsp;@ {exp.company}</span>
              </span>
              <span className="exp-header-right">
                <span className="exp-duration">{exp.duration}</span>
                <span className="exp-toggle-icon">
                  {isOpen ? (
                    <MdRemoveCircleOutline size={22} />
                  ) : (
                    <MdAddCircleOutline size={22} />
                  )}
                </span>
              </span>
            </button>

            {/* Expandable detail panel */}
            <div className={`exp-accordion-body ${isOpen ? "expanded" : ""}`}>
              <div className="exp-body-inner">
                <div className="exp-meta">
                  <span className="exp-meta-item">
                    <FaMapMarkerAlt size={12} />
                    &nbsp;{exp.location}
                  </span>
                  {exp.link && (
                    <span className="exp-meta-item">
                      <FaExternalLinkAlt size={11} />
                      &nbsp;
                      <a href={exp.link} target="_blank" rel="noreferrer" className="exp-link">
                        {exp.link.replace("https://", "")}
                      </a>
                    </span>
                  )}
                </div>
                <p className="exp-description">{exp.description}</p>

                {/* Live Client Deployments (DigitalOrbit) */}
                {exp.clientSites && (
                  <div className="exp-client-sites-section">
                    <h4 className="exp-client-sites-heading">
                      <FaGlobe style={{ marginRight: "8px", color: "#ffffff" }} />
                      Live Deployments — Shipped @ {exp.company}
                    </h4>
                    <ClientLaunches sites={exp.clientSites} />
                  </div>
                )}

                {!exp.clientSites && (
                  <div className="exp-tags">
                    {exp.tags.map((tag, ti) => (
                      <span key={ti} className="exp-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </Container>
  );
}

export default ExperienceAccordion;
